/**
 * useServerSearch — hook condiviso per ricerca server-side con soglia
 * minima caratteri + debounce. Unifica il pattern usato in tutte le
 * liste entita' (Task, Contatti, Aziende, Preventivi):
 *
 *   const { search, setSearch, query } = useServerSearch();
 *   useEffect(() => {
 *     dispatch(fetchX(query ? { q: query } : undefined));
 *   }, [query, dispatch]);
 *
 * `query` e' la stringa da inviare al backend (undefined se sotto soglia),
 * debounced. `search` e' il valore "live" da bindare al TextField.
 */

import { useMemo, useState } from 'react';
import { useDebounce } from './useDebounce';

interface UseServerSearchOptions {
  /** Lunghezza minima della query perche' venga eseguita. Default 3. */
  minChars?: number;
  /** Millisecondi di debounce sulla digitazione. Default 300. */
  delayMs?: number;
}

interface UseServerSearchResult {
  /** Stato live del TextField (input immediato). */
  search: string;
  /** Setter per il TextField. */
  setSearch: (value: string) => void;
  /** Query da passare al fetcher: undefined sotto soglia, altrimenti
   *  stringa trim-mata gia' debounced. */
  query: string | undefined;
}

export function useServerSearch(options?: UseServerSearchOptions): UseServerSearchResult {
  const { minChars = 3, delayMs = 300 } = options ?? {};
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, delayMs);
  const query = useMemo(() => {
    const trimmed = debounced.trim();
    return trimmed.length >= minChars ? trimmed : undefined;
  }, [debounced, minChars]);
  return { search, setSearch, query };
}
